import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

export type SampleLibraryContextTarget =
  | { kind: "sample"; id: string }
  | { kind: "folder"; id: string }
  | { kind: "root" };

export type SampleLibraryMenuAction =
  | "cut"
  | "copy"
  | "paste"
  | "delete"
  | "export"
  | "rename";

type SampleLibraryContextMenuProps = {
  target: SampleLibraryContextTarget;
  point: { x: number; y: number };
  canPaste: boolean;
  onAction: (action: SampleLibraryMenuAction) => void;
  onClose: () => void;
};

/**
 * Portaled Sample Manager context menu (sample, folder, or library root).
 */
export function SampleLibraryContextMenu({
  target,
  point,
  canPaste,
  onAction,
  onClose,
}: SampleLibraryContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) {
      return;
    }
    const margin = 8;
    const width = menu.offsetWidth || 160;
    const height = menu.offsetHeight || 120;
    const left = Math.min(
      Math.max(margin, point.x),
      Math.max(margin, window.innerWidth - width - margin),
    );
    const top = Math.min(
      Math.max(margin, point.y),
      Math.max(margin, window.innerHeight - height - margin),
    );
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  }, [point.x, point.y]);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const isSample = target.kind === "sample";
  const isFolder = target.kind === "folder";

  return createPortal(
    <>
      <div
        className="pointer-events-auto fixed inset-0 z-[200] m-0 cursor-default border-none bg-transparent p-0"
        onPointerDown={event => {
          event.preventDefault();
          onClose();
        }}
        onContextMenu={event => {
          event.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        className="pointer-events-auto fixed z-[201] min-w-[10rem] max-w-[calc(100vw-16px)] rounded-md border border-white/10 bg-lab-panel p-0.5 text-xs text-slate-200 shadow-[0_12px_28px_rgba(0,0,0,0.35)]"
        role="menu"
        style={{ left: point.x, top: point.y }}
      >
        {isSample || isFolder ? (
          <>
            <MenuItem
              onClick={() => {
                onClose();
                onAction("cut");
              }}
            >
              Cut
            </MenuItem>
            <MenuItem
              onClick={() => {
                onClose();
                onAction("copy");
              }}
            >
              Copy
            </MenuItem>
            <MenuItem
              disabled={!canPaste}
              onClick={() => {
                onClose();
                onAction("paste");
              }}
            >
              Paste
            </MenuItem>
            <MenuSep />
            {isSample ? (
              <MenuItem
                onClick={() => {
                  onClose();
                  onAction("export");
                }}
              >
                Export WAV…
              </MenuItem>
            ) : null}
            <MenuItem
              onClick={() => {
                onClose();
                onAction("rename");
              }}
            >
              Rename
            </MenuItem>
            <MenuItem
              onClick={() => {
                onClose();
                onAction("delete");
              }}
            >
              Delete
            </MenuItem>
          </>
        ) : (
          <MenuItem
            disabled={!canPaste}
            onClick={() => {
              onClose();
              onAction("paste");
            }}
          >
            Paste
          </MenuItem>
        )}
      </div>
    </>,
    document.body,
  );
}

function MenuSep() {
  return <div className="my-0.5 h-px bg-white/10" role="separator" />;
}

function MenuItem({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="block h-8 w-full rounded border-none bg-transparent px-[0.55rem] text-left text-inherit [font:inherit] hover:enabled:bg-white/8 disabled:cursor-not-allowed disabled:text-slate-500"
      role="menuitem"
      disabled={disabled}
      onMouseDown={event => {
        if (!disabled) {
          event.preventDefault();
        }
      }}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled) {
          onClick();
        }
      }}
    >
      {children}
    </button>
  );
}
